import React, { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const URGENT_AT = 10;

const CountdownTimer = ({
  sx,
  seconds = 30,
  visible = true,
  running = true,
  onComplete,
  text,
}) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Reset when the duration changes
  useEffect(() => {
    setTimeLeft(seconds);
  }, [seconds]);

  // Single interval-style loop anchored to a fixed end time (no drift,
  // no re-subscribe on every tick, onComplete fires exactly once)
  useEffect(() => {
    if (!running) return;
    const endTime = Date.now() + seconds * 1000;
    let timeoutId;

    const tick = () => {
      const remaining = Math.max(Math.ceil((endTime - Date.now()) / 1000), 0);
      setTimeLeft(remaining);
      if (remaining > 0) {
        timeoutId = setTimeout(tick, 250);
      } else {
        onCompleteRef.current?.();
      }
    };
    tick();

    return () => clearTimeout(timeoutId);
  }, [running, seconds]);

  if (!visible) return null;

  const progress = (timeLeft / seconds) * 100;
  const urgent = timeLeft <= URGENT_AT && timeLeft > 0;
  const color = timeLeft === 0 ? "#9AA0A6" : urgent ? "#D92D20" : "#0E7C86";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: urgent ? "#F5C2C2" : "#E3E8EE",
        bgcolor: urgent ? "#FFF6F6" : "#FFFFFF",
        transition: "background-color .3s, border-color .3s",
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#344054" }}>
        {text}
      </Typography>

      <Box position="relative" display="inline-flex" flexShrink={0}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={52}
          thickness={4}
          sx={{ color: "#E9EDF2" }}
        />
        <CircularProgress
          variant="determinate"
          value={progress}
          size={52}
          thickness={4}
          sx={{
            color,
            position: "absolute",
            left: 0,
            transform: "scaleX(-1) rotate(-90deg) !important", // drain clockwise
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
              transition: "stroke-dashoffset .3s linear",
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {timeLeft}s
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CountdownTimer;